import { DashboardAndAnalysisFiltersPreview } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import { FeedDefinitionInfoItem } from '@modules/feed/components/FeedDefinitionInfo/FeedDefinitionInfoItem';
import { useGetFeedWhereClause } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { alpha } from '@mui/material';
import { AnalysisUseCase } from 'generated/graphql';
import { isEqual } from 'lodash';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface FeedFilterDefinitionInfoProps {
  shouldIncludeDashboardFilters: boolean;
  shouldHideTooltips?: boolean;
  shouldShowDashboardFiltersSeparately: boolean;
}

export const FeedFilterDefinitionInfo = ({
  shouldIncludeDashboardFilters,
  shouldHideTooltips = false,
  shouldShowDashboardFiltersSeparately,
}: FeedFilterDefinitionInfoProps) => {
  const primaryWhereClause = useGetFeedWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const whereClause = useGetFeedWhereClause();

  const dashboardWhereClause = useFeedStore(
    ({ visual }) => visual.dashboardFilters?.whereClause ?? EmptyWhereClause,
  );

  const canShowDashboardFilters =
    !isEqual(dashboardWhereClause, EmptyWhereClause) &&
    shouldIncludeDashboardFilters;

  if (!canShowDashboardFilters) {
    return (
      <FeedDefinitionInfoItem
        leftNode="Filter:"
        rightNode={
          !isEmptyWhereClause(primaryWhereClause) ? (
            <WhereClausePreview
              shouldHideTooltips={shouldHideTooltips}
              whereClause={primaryWhereClause}
              sxProps={{
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.paper, 0.8),
              }}
            />
          ) : (
            'No filters applied'
          )
        }
      />
    );
  }

  if (!shouldShowDashboardFiltersSeparately) {
    return (
      <FeedDefinitionInfoItem
        leftNode="Filter:"
        rightNode={
          !isEmptyWhereClause(whereClause) ? (
            <WhereClausePreview
              shouldHideTooltips={shouldHideTooltips}
              whereClause={whereClause}
              sxProps={{
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.paper, 0.8),
              }}
            />
          ) : (
            'No filters applied'
          )
        }
      />
    );
  }

  return (
    <FeedDefinitionInfoItem
      leftNode="Filter:"
      rightNode={
        <DashboardAndAnalysisFiltersPreview
          dashboardWhereClause={dashboardWhereClause}
          analysisWhereClause={primaryWhereClause}
          shouldHideTooltips={shouldHideTooltips}
          analysisUseCase={AnalysisUseCase.Feed}
        />
      }
    />
  );
};
